/**
 * Where in `/tina/refresh-review` the curator was when they opened an item.
 *
 * **The bug this exists for.** `resolveHitl` navigates on success, and its default is
 * the shared HITL queue — right for every scrape reason, wrong for a refresh park twice
 * over: `/tina/hitl` is scrape-only by owner ruling R55 and deliberately hides refresh
 * rows, so a curator resolving one from `/tina/refresh-review` landed in a queue their
 * item had never appeared in, at the top of a different list. With 1,116 parks open the
 * day this shipped, that is a lost place in the list per resolution.
 *
 * **Why a remembered search string and not just the route.** `/tina/enrichment` solved
 * the same class of bug with `navigateTo: "tina-enrichment"` — a Refine resource name,
 * which resolves to that resource's bare `list` route. That is enough there, because
 * everything that page's curator narrows is a `usePersistedState` control backed by
 * localStorage. It is not enough here: `RefreshReviewList` is a Refine `useTable`, and
 * with the app's global `syncWithLocation: true` its page, sorters and column filters
 * live in the URL's query string. Going to the bare route throws all three away and
 * lands the curator on page 1. The mode picker survives on its own — it is a
 * `usePersistedState` — which is exactly why the page number was the half that kept
 * getting lost.
 *
 * So the queue records its own query string while a curator is on it, and the panel
 * concatenates that onto the one constant path below.
 *
 * **`sessionStorage`, not `localStorage`.** The other refresh-review memory
 * (`tina-refresh-review:mode`) is a preference and belongs in localStorage; this is a
 * cursor, and "I was on page 17 a moment ago" stops being true once the tab is gone. A
 * page index that outlived the session could strand a curator on an out-of-range page of
 * a queue that has since been burned down — an empty table that looks like a broken one.
 *
 * **Only ever a query string.** The path is the constant here; storage holds nothing but
 * the `?…` fragment, and a stored value that does not look like one is ignored. Nothing
 * a page writes can therefore redirect the curator anywhere but this queue.
 */

/** The queue's route, as `App.tsx` mounts it on the `tina-refresh-review` resource. */
export const REFRESH_REVIEW_PATH = "/tina/refresh-review";

const RETURN_SEARCH_KEY = "tina-refresh-review:return-search";

/**
 * Record the queue's current query string. Called from the list on every location
 * change, so the memory tracks paging and filtering without the list having to know
 * which of Refine's params ended up in the URL.
 */
export function rememberRefreshReviewSearch(search: string): void {
  if (typeof window === "undefined") return;
  try {
    // An empty search is a real answer — page 1, no filters — and must clear the
    // memory rather than leave yesterday's page in it.
    if (isQueryString(search)) {
      window.sessionStorage.setItem(RETURN_SEARCH_KEY, search);
    } else {
      window.sessionStorage.removeItem(RETURN_SEARCH_KEY);
    }
  } catch {
    /* private mode or storage disabled — fall back to the bare route */
  }
}

/**
 * The href a resolved refresh park should return the curator to: the queue, at the page
 * and filters they left it on, or its bare route when nothing was recorded (a curator
 * who arrived from a Discord link, a fresh tab, or storage the browser refuses).
 */
export function refreshReviewReturnHref(): string {
  if (typeof window === "undefined") return REFRESH_REVIEW_PATH;
  try {
    const stored = window.sessionStorage.getItem(RETURN_SEARCH_KEY);
    return isQueryString(stored)
      ? `${REFRESH_REVIEW_PATH}${stored}`
      : REFRESH_REVIEW_PATH;
  } catch {
    return REFRESH_REVIEW_PATH;
  }
}

/**
 * A `?…` fragment, which is the only thing this module ever stores or replays.
 *
 * Deliberately no restriction on what follows the `?`: Refine encodes filter values into
 * it, and a site name like `dice.fm/uk` is a legitimate one. The safety does not come
 * from vetting the fragment — it comes from the fragment only ever being appended to
 * this module's own constant path, so nothing it can hold navigates off this queue.
 */
function isQueryString(value: string | null): value is string {
  return typeof value === "string" && value.startsWith("?");
}
